import React, { useMemo } from 'react';
import { useFarmerFormValidation } from '@applicationKYC/LegalEntityQuestionnaire/validation/useFarmerFormValidation';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import breakpointSizes from '@applicationKYC/LegalEntityQuestionnaire/styled-theme-styles/global-style-variables.style';
import useWindowDimensions from '@applicationKYC/LegalEntityQuestionnaire/Utils/window-size.service';
import { Input, Field, Loader, Dropdown } from '@single-platform/components';
import { FarmersQuestionnaireHeader } from '@applicationKYC/FarmersQuestionnaire/farmersQuestionnaire.style';
import {
  FormStyles,
  PageHeader
} from '@applicationKYC/LegalEntityQuestionnaire/screens/renderPage/renderPage.style';
import { ButtonSection } from '@applicationKYC/LegalEntityQuestionnaire/components';
import { ButtonSectionWrapperStyle } from '@applicationKYC/FarmersQuestionnaire/questionnaireSections/formStyles';
import { useSelector } from 'react-redux';
import countries from '@auth/components/countriesDropdown/farmersCountryList.json';
import { RadioChipOption } from '@applicationKYC/FarmersQuestionnaire/components/radioChipOption/radioChipOption';
import { Checkbox } from '@applicationKYC/FarmersQuestionnaire/components';
import { DropdownInput } from '@applicationKYC/FarmersQuestionnaire/components/dropdownInput/dropdownInput';
import {
  identityDocumentTypeOptions,
  yesNoChipOptions
} from '@applicationKYC/FarmersQuestionnaire/constants';
import { selectKycAppState } from '@redux';

const defaultValues = {
  hasDualCitizenship: null,
  type: null,
  documentNumber: null,
  issuingCountry: null,
  acquiredThroughInvestment: false
};

export const RepresentativeDualCitizenshipPage = ({ onSubmit, isAtBottom }) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { loading, farmerInformation } = useSelector((state) => state.FarmersQuestionnaireReducer);
  const applicationState = useSelector(selectKycAppState);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        hasDualCitizenship: Yup.boolean().required(t('validation_required_field')).nullable(),
        type: Yup.string()
          .when('hasDualCitizenship', {
            is: true,
            then: Yup.string().required(t('validation_required_field'))
          })
          .nullable(),
        documentNumber: Yup.string()
          .when('hasDualCitizenship', {
            is: true,
            then: Yup.string().required(t('validation_required_field'))
          })
          .nullable(),
        issuingCountry: Yup.string()
          .when('hasDualCitizenship', {
            is: true,
            then: Yup.string().required(t('validation_required_field'))
          })
          .nullable(),
        acquiredThroughInvestment: Yup.bool()
      }),
    [t]
  );

  const existingValues =
    farmerInformation?.farmRepresentativeSection?.representativeDualCitizenshipPage;
  const initialValues = { ...defaultValues, ...existingValues };

  const { validationHelpers, getValues, setTouchedOnAll, handleSubmit } = useFarmerFormValidation({
    initialValues,
    validationSchema
  });

  const onHandleSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(
      async (data) => {
        await onSubmit?.(data, 'representativeDualCitizenshipPage');
      },
      () => setTouchedOnAll()
    )();
  };

  if (loading) return <Loader />;

  const values = getValues();

  return (
    <FormStyles small={width < breakpointSizes.phone}>
      <FarmersQuestionnaireHeader>
        <PageHeader>{t('farmers_representative_dual_citizenship_header')}</PageHeader>
      </FarmersQuestionnaireHeader>
      <Field
        Component={RadioChipOption}
        options={yesNoChipOptions}
        value={values.hasDualCitizenship}
        name="hasDualCitizenship"
        validationHelpers={validationHelpers}
        validateOnChange
      />
      {values.hasDualCitizenship && (
        <>
          <Field
            Component={Dropdown}
            validationHelpers={validationHelpers}
            name="type"
            value={values.type}
            placeholder={t('farmers_farmer_dual_citizenship_type')}
            options={identityDocumentTypeOptions}
            validateOnChange
            disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
          />
          <Field
            Component={Input}
            name="documentNumber"
            value={values.documentNumber}
            placeholder={t('farmers_farmer_dual_citizenship_documentNumber')}
            validationHelpers={validationHelpers}
            validateOnChange
            required
            disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
          />
          <DropdownInput
            validationHelpers={validationHelpers}
            name="issuingCountry"
            value={values.issuingCountry}
            placeholder={t('farmers_farmer_dual_citizenship_issuingCountry')}
            options={countries}
            validateOnChange
            disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
          />
          <Field
            Component={Checkbox}
            name="acquiredThroughInvestment"
            checked={values.acquiredThroughInvestment}
            value={values.acquiredThroughInvestment}
            validationHelpers={validationHelpers}
            label={t('farmers_farmer_dual_citizenship_acquiredThroughInvestment')}
            validateOnChange
            disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
          />
        </>
      )}
      <ButtonSectionWrapperStyle isAtBottom={isAtBottom}>
        <ButtonSection
          secondButtonClick={onHandleSubmit}
          secondButtonPrimary={true}
          secondButtonText={
            applicationState?.state === 'UserFillingApplication'
              ? t('button_save_and_continue')
              : t('button_continue')
          }
        />
      </ButtonSectionWrapperStyle>
    </FormStyles>
  );
};
